import type {
  Appointment,
  AppointmentHistoryEntry,
  CreateScoutOsaRatingInput,
  ScoutAssignment,
  ScoutOsaRating,
} from '../types'

import type { OsaEnvironment } from '@/shared/types'
import type { EmailItem } from '@/shared/types/email.types'
import { httpClient } from '@/shared/utils/http-client'

// Use relative path in development to leverage Vite proxy
const API_BASE_URL = import.meta.env.DEV
  ? '/user/v1/admin'
  : 'https://scout-service-v2.enter.de/user/v1/admin'

class ApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

class ApiClient {
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`
      let responseBody = null

      try {
        responseBody = await response.json()
        errorMessage = responseBody.message || responseBody.error || errorMessage
        console.error('API Error Response:', responseBody)
      } catch {
        // If response body is not JSON, try to get text
        try {
          const textBody = await response.text()
          console.error('API Error Text:', textBody)
          errorMessage = textBody || errorMessage
        } catch {
          // If that fails too, use default error message
        }
      }

      throw new ApiError(errorMessage, response.status)
    }

    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text()
      console.error('Non-JSON response:', text)
      const jsonError = new Error(`Expected JSON but got ${contentType}: ${text.substring(0, 200)}`)
      throw jsonError
    }

    return response.json() as Promise<T>
  }

  async getAppointments(params?: {
    page?: number
    pageSize?: number
    status?: string | string[]
    query?: string
    sortField?: string
    sortOrder?: string
    env?: OsaEnvironment
    type?: string
    subtype?: string[]
    funnel?: string
    buildingUUID?: string
  }): Promise<{ data: Appointment[]; total: number }> {
    const queryParams = new URLSearchParams()

    // Convert page/pageSize to limit/offset for backend
    const limit = params?.pageSize || 10
    const offset = params?.page ? (params.page - 1) * limit : 0

    queryParams.append('limit', limit.toString())
    queryParams.append('offset', offset.toString())

    if (params?.status) {
      // Handle both single status and array of statuses
      if (Array.isArray(params.status)) {
        params.status.forEach(status => queryParams.append('status', status))
      } else {
        queryParams.append('status', params.status)
      }
    }

    if (params?.query) {
      queryParams.append('q', params.query)
    }

    // Add sorting parameters
    if (params?.sortField) {
      queryParams.append('sortBy', params.sortField)
      queryParams.append('sortOrder', params?.sortOrder || 'desc')
    }

    // Add env parameter
    if (params?.env) {
      queryParams.append('env', params.env)
    }

    // Add type parameter
    if (params?.type) {
      queryParams.append('type', params.type)
    }

    // Add subtype parameter(s)
    if (params?.subtype) {
      params.subtype.forEach(subtype => queryParams.append('subtype', subtype))
    }

    // Add funnel parameter
    if (params?.funnel) {
      queryParams.append('funnel', params.funnel)
    }

    // Add buildingUuid parameter
    if (params?.buildingUUID) {
      queryParams.append('buildingUuid', params.buildingUUID)
    }

    const url = `${API_BASE_URL}/onsite-appointment${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    // console.log('Fetching appointments from:', url) // Debug log

    const response = await httpClient.fetch(url, {
      method: 'GET',
    })

    // The API now returns { data: Appointment[], count: number }
    const result = await this.handleResponse<{ data: Appointment[]; count: number }>(response)

    // console.log('API Response:', result) // Debug log

    return {
      data: result.data,
      total: result.count,
    }
  }

  async getAppointmentById(id: string): Promise<{ data: Appointment }> {
    const response = await httpClient.fetch(`${API_BASE_URL}/onsite-appointment/${id}`, {
      method: 'GET',
    })

    // Check if the API returns the appointment directly or wrapped
    const result = await this.handleResponse<Appointment | { data: Appointment }>(response)

    // If the API returns the appointment directly, wrap it
    if ('uuid' in result) {
      return { data: result }
    }

    return result as { data: Appointment }
  }

  async updateAppointment(id: string, data: Partial<Appointment>): Promise<{ data: Appointment }> {
    // Ensure the UUID is included in the request body
    const requestData = {
      ...data,
      uuid: id,
    }

    console.error('Updating appointment:', id, requestData) // Debug log

    const response = await httpClient.fetch(`${API_BASE_URL}/onsite-appointment/${id}`, {
      method: 'PUT',
      body: JSON.stringify(requestData),
    })

    // Check if the API returns the updated appointment directly or wrapped
    const result = await this.handleResponse<Appointment | { data: Appointment }>(response)

    // If the API returns the appointment directly, wrap it
    if ('uuid' in result) {
      return { data: result }
    }

    return result as { data: Appointment }
  }

  async createOnSiteAppointment(data: {
    leadUUID: string
    firstName: string
    lastName: string
    email: string
    phone: string
    buildingUUID: string
    street: string
    city: string
    postalCode: string
    type: 'isfp' | 'marketplace'
    subtypes?: ('heatpump' | 'pv')[]
  }): Promise<{ data: Appointment }> {
    const response = await httpClient.fetch(`${API_BASE_URL}/onsite-appointment`, {
      method: 'POST',
      body: JSON.stringify(data),
    })

    // Backend returns the created appointment directly (not wrapped in { data })
    const result = await this.handleResponse<Appointment>(response)

    return { data: result }
  }

  async rescheduleAppointment(
    appointmentId: string,
    data: {
      scoutUUID: string
      scheduleStartTime: string
      type: string
    }
  ): Promise<{ data: Appointment }> {
    const response = await httpClient.fetch(
      `${API_BASE_URL}/onsite-appointment/${appointmentId}/reschedule`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )

    // Backend returns the created appointment directly (not wrapped in { data })
    const result = await this.handleResponse<Appointment>(response)

    return { data: result }
  }

  async getScoutAssignments(appointmentId: string): Promise<{ data: ScoutAssignment[] }> {
    const response = await httpClient.fetch(
      `${API_BASE_URL}/onsite-appointment/${appointmentId}/scout-assignments`,
      {
        method: 'GET',
      }
    )

    // The API returns { code: number, data: ScoutAssignment[] }
    const result = await this.handleResponse<{ code: number; data: ScoutAssignment[] }>(response)

    return { data: result.data }
  }

  async retriggerScoutNotifications(
    appointmentId: string
  ): Promise<{ code: number; errors: string[] }> {
    const response = await httpClient.fetch(
      `${API_BASE_URL}/onsite-appointment/${appointmentId}/retrigger-scout-assignment`,
      {
        method: 'POST',
      }
    )

    return this.handleResponse<{ code: number; errors: string[] }>(response)
  }

  async getAppointmentEmails(appointmentId: string): Promise<{ data: EmailItem[] }> {
    const response = await httpClient.fetch(
      `${API_BASE_URL}/onsite-appointment/${appointmentId}/emails`,
      {
        method: 'GET',
      }
    )

    const result = await this.handleResponse<{ data?: EmailItem[] } | EmailItem[]>(response)

    // Handle both direct array response and wrapped response
    if (Array.isArray(result)) {
      return { data: result }
    }

    return { data: result.data || [] }
  }

  async getAppointmentHistory(appointmentId: string): Promise<{ data: AppointmentHistoryEntry[] }> {
    const response = await httpClient.fetch(
      `${API_BASE_URL}/onsite-appointment/${appointmentId}/history`,
      {
        method: 'GET',
      }
    )

    // The API returns { code: number, data: AppointmentHistoryEntry[] }, newest first
    const result = await this.handleResponse<{ code: number; data: AppointmentHistoryEntry[] }>(
      response
    )

    return { data: result.data ?? [] }
  }

  async createScoutAssignment(
    appointmentId: string,
    scoutUUID: string
  ): Promise<{ data: ScoutAssignment }> {
    const response = await httpClient.fetch(
      `${API_BASE_URL}/onsite-appointment/${appointmentId}/scout-assignment`,
      {
        method: 'POST',
        body: JSON.stringify({ scout_uuid: scoutUUID }),
      }
    )

    return this.handleResponse<{ data: ScoutAssignment }>(response)
  }

  async createScoutOsaRating(
    appointmentId: string,
    data: CreateScoutOsaRatingInput
  ): Promise<void> {
    const response = await httpClient.fetch(
      `${API_BASE_URL}/onsite-appointment/${appointmentId}/scout-rating`,
      {
        method: 'POST',
        body: JSON.stringify({
          scout_uuid: data.scoutUUID,
          user_email: data.userEmail,
          photo_sketch_rating: data.photoSketchRating,
          room_capture_rating: data.roomCaptureRating,
          overall_expertise_rating: data.overallExpertiseRating,
          note: data.note || undefined,
        }),
      }
    )

    // Backend responds 201 with an empty body, so skip JSON parsing on success
    if (!response.ok) {
      await this.handleResponse<never>(response)
    }
  }

  async getScoutOsaRatings(appointmentId: string): Promise<{ data: ScoutOsaRating[] }> {
    const response = await httpClient.fetch(
      `${API_BASE_URL}/onsite-appointment/${appointmentId}/scout-ratings`,
      {
        method: 'GET',
      }
    )

    const result = await this.handleResponse<{ data: ScoutOsaRating[]; count: number }>(response)

    return { data: result.data }
  }
}

export const appointmentsApi = new ApiClient()
